/**
 * URL search parameters of the store control screens (shareable like every analytics view, 8.13): period and stores
 * from AnalyticsSearch, plus the event filters, the opened event, and the compare metric / grouping.
 */
import {
  CONTROL_COMPARE_METRICS,
  isControlState,
  type ControlCompareMetric,
  type ControlState,
} from '../api/control_types';
import { validateAnalyticsSearch, type AnalyticsSearch } from './analytics_search';

export const CONTROL_COMPARE_GROUPINGS = ['stores', 'regions'] as const;
export type ControlCompareGrouping = (typeof CONTROL_COMPARE_GROUPINGS)[number];

const ZONE_ID_PATTERN = /^[a-z0-9-]{1,40}$/;
const EVENT_ID_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/;

export interface ControlSearch extends AnalyticsSearch {
  /** Comma-separated states of the event list filter. */
  states?: string;
  /** One zone id of the event list filter. */
  zone?: string;
  /** Event opened in the evidence viewer. */
  event?: string;
  metric?: ControlCompareMetric;
  /** One state the comparison is limited to. */
  state?: ControlState;
  group?: ControlCompareGrouping;
}

function includesValue<Value extends string>(
  allowed: readonly Value[],
  candidate: unknown,
): candidate is Value {
  return typeof candidate === 'string' && (allowed as readonly string[]).includes(candidate);
}

export function validateControlSearch(rawSearch: Record<string, unknown>): ControlSearch {
  const validated: ControlSearch = validateAnalyticsSearch(rawSearch);
  if (typeof rawSearch.states === 'string') {
    const states = rawSearch.states.split(',').filter(isControlState);
    if (states.length > 0) {
      validated.states = [...new Set(states)].join(',');
    }
  }
  if (typeof rawSearch.zone === 'string' && ZONE_ID_PATTERN.test(rawSearch.zone)) {
    validated.zone = rawSearch.zone;
  }
  if (typeof rawSearch.event === 'string' && EVENT_ID_PATTERN.test(rawSearch.event)) {
    validated.event = rawSearch.event;
  }
  if (includesValue(CONTROL_COMPARE_METRICS, rawSearch.metric)) {
    validated.metric = rawSearch.metric;
  }
  if (isControlState(rawSearch.state)) {
    validated.state = rawSearch.state;
  }
  if (includesValue(CONTROL_COMPARE_GROUPINGS, rawSearch.group)) {
    validated.group = rawSearch.group;
  }
  return validated;
}

export function selectedControlStates(search: ControlSearch): ControlState[] {
  return (search.states ?? '').split(',').filter(isControlState);
}
